import keycode from 'keycode';
import PropTypes from 'prop-types';
import compose from "ramda/es/compose";
import cx from "ramda-extension/es/cx";
import React, { Children, Component } from 'react';
import { findDOMNode } from 'react-dom';
import { Popper } from 'react-popper';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import FormGroup from '../../../molecules/FormGroup';
import SlideIn from '../../../molecules/SlideIn';
import { getOffsetWidth } from '../../../utils/dimensions';
import filterChildren from '../../../utils/filterChildren';
import SelectFilters from '../SelectFilters';
import SelectInput from '../SelectInput';
import SelectList from '../SelectList';
const COMPONENT_NAME = 'Autosuggest';
const prop = (pprop)=>(obj)=>obj?.[pprop];
export const getProps = prop('props');
class Autosuggest extends Component {
    static displayName = 'Autosuggest';
    constructor(props){
        super(props);
        this._getValueFromOption = compose(prop(props.valueBy), getProps);
        this._getLabelFromOption = compose(prop(props.filterBy), getProps);
        this._setFilteredChildrenByValue(props.value);
        this.state = {
            target: null,
            isOpen: false,
            focusedIndex: -1,
            selectedIndex: -1
        };
    }
    getInputNode() {
        return this.control.getInputNode ? this.control.getInputNode() : findDOMNode(this.control);
    }
    blur() {
        const node = this.getInputNode();
        if (node) {
            node.blur();
        }
    }
    focus() {
        const node = this.getInputNode();
        if (node) {
            node.focus();
        }
    }
    open(cb) {
        const selected = this._getSelected(this.filteredChildren, this.props.value);
        const focusedIndex = this.filteredChildren.indexOf(selected);
        this.setState({
            target: findDOMNode(this),
            isOpen: this.filteredChildren,
            focusedIndex: focusedIndex > 0 ? focusedIndex : 0,
            selectedIndex: selected ? focusedIndex : -1
        }, cb);
        this.props.onFocus?.();
    }
    _filterOptions(children, value) {
        return !value ? children : filterChildren((c)=>this.props.filter(this._getLabelFromOption(c), value), children);
    }
    close() {
        this.setState({
            isOpen: false
        }, ()=>{
            this.blur();
            this.props.onBlur?.();
        });
    }
    _getValueLabel() {
        const { children, value } = this.props;
        return this._getLabelFromOption(this._getSelected(children, value)) || value;
    }
    _getSelected(children, value) {
        let option = null;
        Children.forEach(children, (child)=>{
            if (this._getValueFromOption(child) === value || this._getLabelFromOption(child) === value) {
                option = child;
                return option;
            }
        });
        return option;
    }
    _setFocusedIndex(index) {
        this.setState({
            focusedIndex: index
        });
    }
    _changeValue = (event, option)=>{
        const value = this._getValueFromOption(option);
        this._setFilteredChildrenByValue(value);
        this._setFocusedIndex(0);
        this.props.onChange?.(event, value);
    };
    _incrementFocusedIndex() {
        const maxIndex = Children.count(this.filteredChildren) - 1;
        let focusedIndex = this.state.focusedIndex;
        focusedIndex++;
        if (focusedIndex <= maxIndex) {
            this._setFocusedIndex(focusedIndex);
        }
    }
    _decrementFocusedIndex() {
        let focusedIndex = this.state.focusedIndex;
        focusedIndex--;
        if (focusedIndex >= 0) {
            this._setFocusedIndex(focusedIndex);
        }
    }
    _handleInputFocus = ()=>{
        this.open();
    };
    _setFilteredChildrenByValue = (value)=>{
        this.filteredChildren = this._filterOptions(this.props.children, value);
    };
    _handleInputChange = (e, value)=>{
        this._setFilteredChildrenByValue(value);
        this._setFocusedIndex(0);
        this.props.onChange?.(e, value);
    };
    _handleListClickOutside = ()=>{
        this.close();
    };
    _handleSelect = (e, option)=>{
        this._changeValue(e, option);
        this.close();
    };
    _handleEnter = (e)=>{
        let option;
        const focusedIndex = this.state.focusedIndex;
        if (this.filteredChildren?.[focusedIndex]) {
            option = this.filteredChildren[focusedIndex];
        }
        this._changeValue(e, option);
    };
    _handleInputKeyDown = (event)=>{
        switch(keycode(event)){
            case 'enter':
                this._handleEnter(event);
                this.close();
                break;
            case 'down':
                event.preventDefault();
                this._incrementFocusedIndex();
                break;
            case 'up':
                event.preventDefault();
                this._decrementFocusedIndex();
                break;
            case 'tab':
                this.close();
                break;
            case 'esc':
                this.close();
                break;
            default:
        }
    };
    _renderList(children) {
        const { disabled, selectListAutoWidth } = this.props;
        const { focusedIndex, selectedIndex, target, isOpen } = this.state;
        const list = !isOpen || disabled || !children.length ? null : /*#__PURE__*/ React.createElement(SelectList, {
            ref: (list)=>{
                this._list = list;
            },
            key: "list",
            searchText: this._getValueLabel(),
            onSelect: this._handleSelect,
            onClickOutside: this._handleListClickOutside,
            focusedIndex: focusedIndex,
            selectedIndex: selectedIndex,
            style: {
                width: !selectListAutoWidth && target ? getOffsetWidth(target) : 'auto'
            }
        }, children);
        return /*#__PURE__*/ React.createElement(SlideIn, null, /*#__PURE__*/ React.createElement("div", {
            key: "divList"
        }, list));
    }
    render() {
        const { autoFocus, asyncValidationInProgress, id, labelText, labelNote, labelPosition, labelClassName, className, floatingLabelText, floatingLabelClassName, controlClassName, controlInputClassName, validationText, validationClassName, disabled, placeholder, tabIndex, onBlur } = this.props;
        const { target } = this.state;
        const SelectInputProps = {
            id,
            autoFocus,
            labelText,
            labelNote,
            labelPosition,
            labelClassName,
            floatingLabelText,
            floatingLabelClassName,
            controlClassName,
            controlInputClassName,
            validationText,
            validationClassName,
            disabled,
            placeholder,
            asyncValidationInProgress,
            tabIndex,
            value: this._getValueLabel(),
            ref: (el)=>{
                this.control = el;
            },
            onChange: this._handleInputChange,
            onFocus: this._handleInputFocus,
            onKeyDown: this._handleInputKeyDown,
            onBlur
        };
        this._setFilteredChildrenByValue(this._getValueLabel());
        const list = this._renderList(this.filteredChildren);
        return /*#__PURE__*/ React.createElement("div", {
            className: cx('Select', className),
            ref: (el)=>{
                this.rootEl = el;
            }
        }, /*#__PURE__*/ React.createElement(SelectInput, SelectInputProps), target ? /*#__PURE__*/ React.createElement(Popper, {
            referenceElement: target,
            placement: "bottom-start"
        }, ({ style, ref })=>/*#__PURE__*/ React.createElement("div", {
                className: 'Select-popper',
                ref: ref,
                style: style
            }, list)) : null);
    }
}
Autosuggest.propTypes = {
    /**
	 * True if async validation is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    /**
	 * Set autoFocus on input element
	 */ autoFocus: PropTypes.bool,
    /**
	 * Children will be rendered within the `AutoSuggest` as options.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Custom `className` for the control component.
	 */ controlClassName: PropTypes.string,
    /**
	 * Custom `className` for the control input component.
	 */ controlInputClassName: PropTypes.string,
    /**
	 * If true, component will be disabled
	 */ disabled: PropTypes.bool,
    /**
	 * Custom filter function. Can be value from SelectFilters
	 *
	 * @param {String} value - value of children
	 * @param {String} valueText - text of option value
	 * @return {Boolean} true, if valueText is part of value
	 */ filter: PropTypes.func,
    /**
	 * To which value will be filter applied
	 */ filterBy: PropTypes.string,
    /**
	 * Custom `className` for the floating label.
	 */ floatingLabelClassName: PropTypes.string,
    /**
	 * Floating label will be displayed inside of component
	 */ floatingLabelText: PropTypes.node,
    /**
	 * Id of `TextField` component
	 */ id: PropTypes.string,
    /**
	 * Custom `className` for the label.
	 */ labelClassName: PropTypes.string,
    /**
	 * LabelNote for the `TextField` component
	 */ labelNote: PropTypes.node,
    /**
	 * Position of label (`left`, `right`)
	 */ labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
    /**
	 * Text of label
	 */ labelText: PropTypes.node,
    /**
	 * On blur callback
	 *
	 * @param {Object} event - event targeting the element
	 * @return {undefined} Nothing
	 */ onBlur: PropTypes.func,
    /**
	 * On change callback
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of AutoSuggest
	 * @return {undefined} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * On focus callback
	 *
	 * @param {Object} event event targeting the element
	 * @return {undefined} Nothing
	 */ onFocus: PropTypes.func,
    /**
	 * On input change callback
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of AutoSuggest
	 * @return {undefined} Nothing
	 */ onInputChange: PropTypes.func,
    /**
	 * Text of placeholder
	 */ placeholder: PropTypes.string,
    /**
	 * When true, width for AutoSuggest-list will be set to auto.
	 */ selectListAutoWidth: PropTypes.bool,
    /**
	 * Input's tabindex.
	 */ tabIndex: PropTypes.string,
    /**
	 * Custom `className` for validation.
	 */ validationClassName: PropTypes.string,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Value of AutoSuggest
	 */ value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
        PropTypes.object
    ]),
    /**
	 * From which props should be value taken
	 */ valueBy: PropTypes.string
};
export { Autosuggest };
export default withDefaultComponentProps(COMPONENT_NAME, {
    filter: SelectFilters.caseInsensitive,
    valueBy: 'value',
    filterBy: 'label'
}, Autosuggest);
